import React from "react";
import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import { ArrowLeft, Lock } from "@phosphor-icons/react";

export const PRIVACY_LAST_UPDATED = "15 February 2026";

export default function PrivacyPolicy() {
    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="privacy-page">
            <header className="bg-ink text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Brand variant="light" size="h-7" to={null} />
                    </Link>
                    <Link
                        to="/"
                        data-testid="privacy-back-home"
                        className="text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white inline-flex items-center gap-1"
                    >
                        <ArrowLeft size={12} weight="bold" /> Home
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                <div className="inline-flex items-center gap-2 mb-5 bg-cobalt text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
                    <Lock size={12} weight="bold" />
                    Legal · Privacy Policy
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
                    Privacy <span className="text-cobalt">Policy.</span>
                </h1>
                <p className="text-xs text-[#A3A3A3] mb-10">
                    Version <span className="font-mono">v1.0-2026-02</span> · Last updated {PRIVACY_LAST_UPDATED} ·
                    Compliant with India&apos;s DPDPA 2023 and GDPR
                </p>

                <div className="prose prose-sm max-w-none text-[#262626] leading-relaxed space-y-6">
                    <Section title="1. What we collect">
                        <p className="mb-3">When you use NaukriDaddy, we collect:</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Identity</strong> — name, email, phone number, password (hashed).</li>
                            <li><strong>Profile</strong> — resume text, work history, skills, current title, location, LinkedIn URL, years of experience.</li>
                            <li><strong>Application activity</strong> — jobs you apply to, referrals you share, testimonials you submit.</li>
                            <li><strong>Technical</strong> — IP address, browser/device fingerprint, timestamps — collected to fight abuse and audit consent.</li>
                            <li><strong>Payments</strong> — for paid plans, we store the plan and transaction ID. Card details never touch our servers; they go directly to Razorpay.</li>
                        </ul>
                    </Section>

                    <Section title="2. Why we process it (legal basis)">
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Contract</strong> — to run your account, match you to jobs, deliver paid features.</li>
                            <li><strong>Consent</strong> — to share your profile with specific recruiters when you apply, send job alerts, marketing.</li>
                            <li><strong>Legitimate interest</strong> — fraud prevention, product analytics (aggregated, non-identifying).</li>
                            <li><strong>Legal obligation</strong> — tax records for paid transactions.</li>
                        </ul>
                    </Section>

                    <Section title="3. Who we share it with">
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Recruiters on jobs you apply to</strong> — your full profile becomes visible to that recruiter&apos;s team.</li>
                            <li><strong>Our AI matching engine</strong> — profile text is processed by our AI models to compute match scores. This runs on our infrastructure; content is not used to train third-party models.</li>
                            <li><strong>Razorpay</strong> — payment processing.</li>
                            <li><strong>Google (opt-in)</strong> — when a recruiter connects Gmail/Calendar, that recruiter&apos;s tokens are stored to send interview invites.</li>
                            <li><strong>Never sold</strong> — we do not sell your data to advertisers or list brokers.</li>
                        </ul>
                    </Section>

                    <Section title="4. Retention">
                        <p>
                            We retain profile data while your account is active. If you
                            delete your account, we purge personal data within 30 days,
                            except: (a) invoices and payment records kept for 7 years per
                            tax law, and (b) anonymized aggregate metrics.
                        </p>
                    </Section>

                    <Section title="5. Your rights">
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Access</strong> — request a copy of everything we hold on you.</li>
                            <li><strong>Correction</strong> — fix inaccurate data via your profile page.</li>
                            <li><strong>Erasure</strong> — delete your account and personal data.</li>
                            <li><strong>Withdraw consent</strong> — unsubscribe from alerts, revoke marketing consent.</li>
                            <li><strong>Portability</strong> — export your profile + application history.</li>
                        </ul>
                        <p className="mt-3">
                            To exercise any of these, email{" "}
                            <a href="mailto:privacy@naukridaddy.com" className="text-cobalt font-bold underline">
                                privacy@naukridaddy.com
                            </a>{" "}
                            — we respond within 30 days.
                        </p>
                    </Section>

                    <Section title="6. Security">
                        <p>
                            Passwords are hashed with bcrypt. Data is encrypted in transit
                            (HTTPS) and at rest. We keep audit trails of consent, logins,
                            and privileged actions.
                        </p>
                    </Section>

                    <Section title="7. Cookies">
                        <p>
                            We use strictly-necessary cookies for authentication (httpOnly,
                            SameSite=None+Secure). We do not use tracking cookies or ad
                            pixels on candidate flows.
                        </p>
                    </Section>

                    <Section title="8. Cross-border transfers">
                        <p>
                            Data is primarily stored in India. Backups and some processing
                            (e.g., anti-abuse) may occur in the EU. All transfers rely on
                            appropriate safeguards (standard contractual clauses).
                        </p>
                    </Section>

                    <Section title="9. Children">
                        <p>
                            NaukriDaddy is not intended for anyone under 18. We do not
                            knowingly collect data from minors.
                        </p>
                    </Section>

                    <Section title="10. Changes to this policy">
                        <p>
                            If we materially change this policy, we&apos;ll notify you by
                            email and require re-consent inside the app.
                        </p>
                    </Section>

                    <Section title="11. Grievance officer (India DPDPA)">
                        <p>
                            Grievance Officer — Data Protection · NaukriDaddy ·{" "}
                            <a href="mailto:grievance@naukridaddy.com" className="text-cobalt font-bold underline">
                                grievance@naukridaddy.com
                            </a>
                        </p>
                    </Section>
                </div>

                <div className="mt-14 pt-6 border-t border-[#E5E5E5] flex flex-wrap items-center gap-4 text-xs text-[#525252]">
                    <Link to="/legal/terms" data-testid="privacy-goto-terms" className="font-bold hover:text-cobalt underline underline-offset-2">
                        Read the Terms of Service →
                    </Link>
                    <Link to="/candidate/signup" data-testid="privacy-goto-signup" className="font-bold hover:text-cobalt underline underline-offset-2">
                        Back to signup →
                    </Link>
                </div>
            </main>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <h2 className="font-heading text-lg font-black tracking-tight mb-2 text-ink">{title}</h2>
            <div className="text-[#404040]">{children}</div>
        </section>
    );
}
